import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { ConditionSchemaField, ConditionSchemaView } from '@/types/api';

/**
 * A rule row in the builder.
 *
 * `uid` is a client-only key. Rows have no server identity — the API replaces
 * the whole condition list on save — so React needs something stable to key
 * on that survives insert/remove in the middle of the list. Array index would
 * make React reuse the wrong input's DOM node on delete.
 */
export interface DraftCondition {
  uid: number;
  field: string;
  operator: string;
  values: string[];
}

let nextUid = 1;
export const newCondition = (): DraftCondition => ({
  uid: nextUid++,
  field: '',
  operator: '',
  values: [],
});

/** Operators that take no operand — the value input is hidden entirely. */
const NO_VALUE_OPERATORS = new Set(['is_true', 'is_false', 'is_null', 'is_not_null']);
/** Operators whose operand is a whole number of days. */
const DAY_OPERATORS = new Set([
  'days_ago_exactly',
  'days_ago_at_least',
  'days_ago_at_most',
  'days_from_now_exactly',
  'days_from_now_at_least',
  'days_from_now_at_most',
]);
/** Operators whose operand is a plain count. */
const NUMBER_OPERATORS = new Set(['eq_num', 'gte', 'lte']);
/** Operators whose operand is a calendar date. */
const DATE_OPERATORS = new Set(['before', 'after']);
/** Operators taking a multi-select of enum values. */
const MULTI_OPERATORS = new Set(['in', 'not_in']);

const OPERATOR_LABELS: Record<string, string> = {
  is_true: 'is true',
  is_false: 'is false',
  eq: 'is',
  neq: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  days_ago_exactly: 'was exactly … days ago',
  days_ago_at_least: 'was at least … days ago',
  days_ago_at_most: 'was within the last … days',
  days_from_now_exactly: 'is exactly … days from now',
  days_from_now_at_least: 'is at least … days from now',
  days_from_now_at_most: 'is within the next … days',
  before: 'is before',
  after: 'is on or after',
  is_null: 'is not set',
  is_not_null: 'is set',
  eq_num: 'equals',
  gte: 'is at least',
  lte: 'is at most',
};

const selectClass =
  'h-9 rounded-lg border border-or-slate-200 bg-white px-2.5 text-[13px] text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30';

interface Props {
  schema: ConditionSchemaView | null;
  conditions: DraftCondition[];
  onChange: (next: DraftCondition[]) => void;
  disabled?: boolean;
}

export function AutomationConditionRows({ schema, conditions, onChange, disabled }: Props) {
  const fieldByName = new Map((schema?.fields ?? []).map((f) => [f.name, f]));

  const patch = (uid: number, next: Partial<DraftCondition>) =>
    onChange(conditions.map((c) => (c.uid === uid ? { ...c, ...next } : c)));

  const remove = (uid: number) => onChange(conditions.filter((c) => c.uid !== uid));

  return (
    <div className="flex flex-col gap-3">
      {conditions.length === 0 ? (
        <p className="rounded-lg border border-dashed border-or-slate-200 px-4 py-6 text-center text-[12px] text-or-slate-500">
          No rules yet. An automation needs at least one rule before it can be turned on — without
          one it would send to the entire list.
        </p>
      ) : null}

      {conditions.map((c, i) => {
        const def = fieldByName.get(c.field) ?? null;
        return (
          <div
            key={c.uid}
            className="flex flex-wrap items-center gap-2 rounded-xl border border-or-slate-200 bg-or-slate-50/60 px-3 py-2.5"
          >
            <span className="w-10 shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-400">
              {i === 0 ? 'Where' : 'And'}
            </span>

            <select
              value={c.field}
              disabled={disabled}
              onChange={(e) => {
                // Changing the field invalidates the operator and operands —
                // a timestamp operator makes no sense on an enum column.
                const nextDef = fieldByName.get(e.target.value);
                patch(c.uid, {
                  field: e.target.value,
                  operator: nextDef?.operators[0] ?? '',
                  values: [],
                });
              }}
              className={selectClass}
            >
              <option value="">Choose a field…</option>
              {(schema?.fields ?? []).map((f) => (
                <option key={f.name} value={f.name}>
                  {f.label}
                </option>
              ))}
            </select>

            <select
              value={c.operator}
              disabled={disabled || !def}
              onChange={(e) => patch(c.uid, { operator: e.target.value, values: [] })}
              className={selectClass}
            >
              {(def?.operators ?? []).map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_LABELS[op] ?? op}
                </option>
              ))}
            </select>

            <ValueInput
              def={def}
              operator={c.operator}
              values={c.values}
              disabled={disabled}
              onChange={(values) => patch(c.uid, { values })}
            />

            <button
              type="button"
              onClick={() => remove(c.uid)}
              disabled={disabled}
              aria-label={`Remove rule ${i + 1}`}
              className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-500 hover:bg-or-rose-100 hover:text-or-rose-700 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        );
      })}

      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...conditions, newCondition()])}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add rule
        </Button>
      </div>
    </div>
  );
}

interface ValueInputProps {
  def: ConditionSchemaField | null;
  operator: string;
  values: string[];
  disabled?: boolean;
  onChange: (values: string[]) => void;
}

/** The operand editor, shaped by the operator rather than the field type. */
function ValueInput({ def, operator, values, disabled, onChange }: ValueInputProps) {
  if (!def || !operator || NO_VALUE_OPERATORS.has(operator)) return null;

  if (DAY_OPERATORS.has(operator)) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min={0}
          max={3650}
          step={1}
          value={values[0] ?? ''}
          disabled={disabled}
          onChange={(e) => onChange([e.target.value])}
          className={`${selectClass} w-20`}
          aria-label="Number of days"
        />
        <span className="text-[12px] text-or-slate-500">days</span>
      </div>
    );
  }

  if (NUMBER_OPERATORS.has(operator)) {
    return (
      <input
        type="number"
        min={0}
        step={1}
        value={values[0] ?? ''}
        disabled={disabled}
        onChange={(e) => onChange([e.target.value])}
        className={`${selectClass} w-24`}
        aria-label="Number"
      />
    );
  }

  if (DATE_OPERATORS.has(operator)) {
    return (
      <input
        type="date"
        value={values[0] ?? ''}
        disabled={disabled}
        onChange={(e) => onChange([e.target.value])}
        className={selectClass}
        aria-label="Date"
      />
    );
  }

  if (MULTI_OPERATORS.has(operator)) {
    return (
      <select
        multiple
        value={values}
        disabled={disabled}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions, (o) => o.value))}
        className={`${selectClass} h-auto min-h-[2.25rem] py-1`}
        aria-label="Values"
      >
        {(def.values ?? []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  }

  // eq / neq on an enum field.
  return (
    <select
      value={values[0] ?? ''}
      disabled={disabled}
      onChange={(e) => onChange([e.target.value])}
      className={selectClass}
      aria-label="Value"
    >
      <option value="">Choose…</option>
      {(def.values ?? []).map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

/** A row is only sendable to the API once its operand arity is satisfied. */
export function isConditionComplete(c: DraftCondition): boolean {
  if (!c.field || !c.operator) return false;
  if (NO_VALUE_OPERATORS.has(c.operator)) return true;
  if (MULTI_OPERATORS.has(c.operator)) return c.values.length > 0;
  if (DAY_OPERATORS.has(c.operator) || NUMBER_OPERATORS.has(c.operator)) {
    // A bare "-" or an empty number input must not count as complete, or the
    // server rejects the whole save after the fact.
    return /^\d+$/.test((c.values[0] ?? '').trim());
  }
  return c.values.length === 1 && (c.values[0] ?? '').trim() !== '';
}
